import type { FlightMode, ForecastModelSelection, SiteOption, UnitProfile } from "../types";

export interface CanonicalSiteState {
  kind: "site";
  site: SiteOption;
  dayOffset: number;
  mode: FlightMode;
  selectedHour: number;
  units: UnitProfile;
  model: ForecastModelSelection;
}

export interface SiteChooserState {
  kind: "chooser";
  reason: "missing_site" | "invalid_url_site";
  dayOffset: number;
  mode: FlightMode;
  selectedHour: number;
  units: UnitProfile;
  model: ForecastModelSelection;
}

export type ResolvedSiteState = CanonicalSiteState | SiteChooserState;

export interface SiteStateIndex {
  byCanonicalPath: Map<string, SiteOption>;
  bySlug: Map<string, SiteOption>;
}

export interface ResolveInitialSiteStateArgs {
  pathname: string;
  search?: string | URLSearchParams;
  sites: readonly SiteOption[];
  storage?: Storage | null;
}

const LAST_SITE_STORAGE_KEY = "lifty:v1:last-site";
const UNIT_PREFERENCE_STORAGE_KEY = "lifty:v1:units";
const DEFAULT_DAY_OFFSET = 0;
const DEFAULT_SELECTED_HOUR = 13;
const DEFAULT_MODE: FlightMode = "free_flight";
const DEFAULT_UNITS: UnitProfile = "us";
const CANONICAL_ROUTE_LENGTH = 4;
const MAX_SLUG_LENGTH = 120;
const SAFE_SLUG = /^[a-z0-9]+(?:[a-z0-9-]*[a-z0-9])?$/;

export function buildSiteStateIndex(sites: readonly SiteOption[]): SiteStateIndex {
  const byCanonicalPath = new Map<string, SiteOption>();
  const bySlug = new Map<string, SiteOption>();
  for (const site of sites) {
    byCanonicalPath.set(normalizePathname(site.canonical_path), site);
    bySlug.set(site.slug, site);
  }
  return { byCanonicalPath, bySlug };
}

export function serializeCanonicalSiteHref(state: CanonicalSiteState): string {
  const params = new URLSearchParams({
    day: String(clampInteger(state.dayOffset, 0, 6, DEFAULT_DAY_OFFSET)),
    mode: state.mode === "ppg" ? "ppg" : "pg",
    hour: String(clampInteger(state.selectedHour, 0, 23, DEFAULT_SELECTED_HOUR)),
    units: state.units
  });
  params.set("model", state.model);
  return `${state.site.canonical_path}?${params.toString()}`;
}

export function resolveInitialSiteState({
  pathname,
  search,
  sites,
  storage = getSafeLocalStorage()
}: ResolveInitialSiteStateArgs): ResolvedSiteState {
  const index = buildSiteStateIndex(sites);
  const route = resolveRouteTarget(normalizePathname(pathname), index);
  const query = parseSiteStateQuery(search);
  const savedUnits = readSavedUnitPreference(storage);
  const savedSite = resolveSavedSite(index, storage);

  if (route.kind === "canonical" && route.site === null) {
    return {
      kind: "chooser",
      reason: "invalid_url_site",
      dayOffset: query.dayOffset,
      mode: query.mode,
      selectedHour: query.selectedHour,
      units: query.units ?? savedUnits ?? DEFAULT_UNITS
      ,model: query.model
    };
  }

  const selectedSite =
    route.kind === "canonical"
      ? route.site
      : route.kind === "root"
        ? savedSite
        : null;

  if (selectedSite) {
    return {
      kind: "site",
      site: selectedSite,
      dayOffset: query.dayOffset,
      mode: query.mode,
      selectedHour: query.selectedHour,
      units: query.units ?? savedUnits ?? defaultUnitsForCountry(selectedSite.country_code)
      ,model: query.model
    };
  }

  return {
    kind: "chooser",
    reason: "missing_site",
    dayOffset: query.dayOffset,
    mode: query.mode,
    selectedHour: query.selectedHour,
    units: query.units ?? savedUnits ?? DEFAULT_UNITS
    ,model: query.model
  };
}

export function readSavedSiteSlug(
  storage: Storage | null | undefined = getSafeLocalStorage()
): string | null {
  const value = readStorageValue(storage, LAST_SITE_STORAGE_KEY);
  return isSafeSlug(value) ? value : null;
}

export function writeSavedSiteSlug(
  slug: string | null,
  storage: Storage | null | undefined = getSafeLocalStorage()
): boolean {
  if (slug === null) {
    return writeStorageValue(storage, LAST_SITE_STORAGE_KEY, null);
  }
  if (!isSafeSlug(slug)) {
    return false;
  }
  return writeStorageValue(storage, LAST_SITE_STORAGE_KEY, slug);
}

export function readSavedUnitPreference(
  storage: Storage | null | undefined
): UnitProfile | null {
  const value = readStorageValue(storage, UNIT_PREFERENCE_STORAGE_KEY);
  return value === "us" || value === "metric" ? value : null;
}

export function writeSavedUnitPreference(
  units: UnitProfile | null,
  storage: Storage | null | undefined = getSafeLocalStorage()
): boolean {
  if (units === null) {
    return writeStorageValue(storage, UNIT_PREFERENCE_STORAGE_KEY, null);
  }
  return writeStorageValue(storage, UNIT_PREFERENCE_STORAGE_KEY, units);
}

function defaultUnitsForCountry(countryCode: string): UnitProfile {
  return countryCode.toLowerCase() === "us" ? "us" : "metric";
}

function getSafeLocalStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function parseSiteStateQuery(search: string | URLSearchParams | undefined): {
  dayOffset: number;
  mode: FlightMode;
  selectedHour: number;
  units: UnitProfile | null;
  model: ForecastModelSelection;
} {
  const params =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(search?.startsWith("?") ? search.slice(1) : search);

  return {
    dayOffset: parseIntegerParam(params.get("day"), 0, 6, DEFAULT_DAY_OFFSET),
    mode: parseModeParam(params.get("mode")),
    selectedHour: parseIntegerParam(params.get("hour"), 0, 23, DEFAULT_SELECTED_HOUR),
    units: parseUnitsParam(params.get("units")),
    model: parseModelParam(params.get("model"))
  };
}

function parseModelParam(value: string | null): ForecastModelSelection {
  if (!value || value.length > 120 || !/^[a-z0-9]+(?:[a-z0-9_-]*[a-z0-9])?$/.test(value)) {
    return "auto";
  }
  return value;
}

function parseModeParam(value: string | null): FlightMode {
  if (value === "ppg") {
    return "ppg";
  }
  return value === "pg" ? "free_flight" : DEFAULT_MODE;
}

function parseUnitsParam(value: string | null): UnitProfile | null {
  return value === "us" || value === "metric" ? value : null;
}

function parseIntegerParam(
  value: string | null,
  minimum: number,
  maximum: number,
  fallback: number
): number {
  if (value === null || value.trim() === "") {
    return fallback;
  }
  if (!/^-?\d+$/.test(value)) {
    return fallback;
  }
  return clampInteger(Number(value), minimum, maximum, fallback);
}

function clampInteger(
  value: number,
  minimum: number,
  maximum: number,
  fallback: number
): number {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    return fallback;
  }
  return value;
}

function normalizePathname(pathname: string | null | undefined): string {
  const trimmed = typeof pathname === "string" ? pathname.trim() : "";
  if (!trimmed) {
    return "/";
  }
  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  const withoutDuplicates = withLeadingSlash.replace(/\/{2,}/g, "/");
  if (withoutDuplicates.length === 1) {
    return "/";
  }
  return withoutDuplicates.replace(/\/+$/, "").toLowerCase();
}

function resolveSavedSite(
  index: SiteStateIndex,
  storage: Storage | null | undefined
): SiteOption | null {
  const slug = readSavedSiteSlug(storage);
  return slug === null ? null : index.bySlug.get(slug) ?? null;
}

function resolveRouteTarget(
  pathname: string,
  index: SiteStateIndex
):
  | { kind: "root" }
  | { kind: "other" }
  | { kind: "canonical"; site: SiteOption | null } {
  if (pathname === "/") {
    return { kind: "root" };
  }

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length !== CANONICAL_ROUTE_LENGTH || segments[0] !== "forecast") {
    return { kind: "other" };
  }

  return {
    kind: "canonical",
    site: index.byCanonicalPath.get(pathname) ?? null
  };
}

function isSafeSlug(value: string | null): value is string {
  return (
    value !== null &&
    value.length > 0 &&
    value.length <= MAX_SLUG_LENGTH &&
    SAFE_SLUG.test(value)
  );
}

function readStorageValue(
  storage: Storage | null | undefined,
  key: string
): string | null {
  if (!storage) {
    return null;
  }
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageValue(
  storage: Storage | null | undefined,
  key: string,
  value: string | null
): boolean {
  if (!storage) {
    return false;
  }
  try {
    if (value === null) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, value);
    }
    return true;
  } catch {
    return false;
  }
}
